import { Tag, Tooltip, Typography } from "antd";
import { VideoOff, WifiOff } from "lucide-react";
import { useTranslation } from "react-i18next";
import type { TFunction } from "i18next";
import { ExamPeriodMonitorState } from "@types";
import type { ProctoringCandidate } from "@hooks";
import { CandidateVideo } from "./CandidateVideo";
import type { ParticipantMedia } from "./useProctoringRoom";

export const candidateName = (c: ProctoringCandidate) =>
  `${c.firstName ?? ""} ${c.lastName ?? ""}`.trim() || c.email || c.examInstanceId;

export const examStateTag = (state: number, t: TFunction) => {
  switch (state) {
    case ExamPeriodMonitorState.WaitingForParticipant:
      return <Tag color="gold">{t("pages.examPeriods.waitingForParticipant")}</Tag>;
    case ExamPeriodMonitorState.ParticipantConfirmed:
      return <Tag color="blue">{t("pages.examPeriods.participantConfirmed")}</Tag>;
    case ExamPeriodMonitorState.InProgress:
      return <Tag color="green">{t("pages.examPeriods.inProgress")}</Tag>;
    case ExamPeriodMonitorState.Paused:
      return <Tag color="orange">{t("pages.examPeriods.paused")}</Tag>;
    case ExamPeriodMonitorState.Excluded:
      return <Tag color="red">{t("pages.examPeriods.disqualified")}</Tag>;
    case ExamPeriodMonitorState.Completed:
      return <Tag>{t("pages.examPeriods.completed")}</Tag>;
    case ExamPeriodMonitorState.NoShow:
      return <Tag>{t("pages.examPeriods.notShowed")}</Tag>;
    default:
      return null;
  }
};

interface Props {
  candidate: ProctoringCandidate;
  media?: ParticipantMedia;
  compact: boolean;
  showAssessor: boolean;
  onClick: () => void;
}

export const CandidateTile = ({ candidate, media, compact, showAssessor, onClick }: Props) => {
  const { t } = useTranslation();
  const name = candidateName(candidate);
  const connected = !!media?.connected;

  return (
    <div
      onClick={onClick}
      data-testid="proctor-tile"
      data-identity={candidate.identity}
      data-connected={connected}
      style={{
        position: "relative",
        background: "#111",
        borderRadius: 6,
        overflow: "hidden",
        cursor: "pointer",
        minHeight: 0,
        outline: connected ? "2px solid #52c41a" : "2px solid #434343",
      }}
    >
      {media?.video ? (
        <CandidateVideo track={media.video} />
      ) : (
        <div style={{ height: "100%", display: "flex", alignItems: "center", justifyContent: "center", color: "#8c8c8c", gap: 6 }}>
          {connected ? <VideoOff size={compact ? 16 : 24} /> : <WifiOff size={compact ? 16 : 24} />}
          {!compact && <span>{connected ? t("proctoring.noVideo") : t("proctoring.notConnected")}</span>}
        </div>
      )}
      <div
        style={{
          position: "absolute",
          left: 0,
          right: 0,
          bottom: 0,
          padding: compact ? "2px 6px" : "4px 8px",
          background: "linear-gradient(0deg, rgba(0,0,0,0.75) 0%, transparent 100%)",
          display: "flex",
          alignItems: "center",
          gap: 6,
        }}
      >
        <span style={{ width: 8, height: 8, borderRadius: "50%", flex: "none", background: connected ? "#52c41a" : "#8c8c8c" }} />
        <Tooltip title={candidate.email}>
          <Typography.Text style={{ color: "#fff", fontSize: compact ? 11 : 13 }} ellipsis>
            {name}
          </Typography.Text>
        </Tooltip>
        {!compact && <span style={{ marginLeft: "auto" }}>{examStateTag(candidate.examInstanceState, t)}</span>}
      </div>
      {showAssessor && !compact && candidate.assessorName && (
        <Tag style={{ position: "absolute", top: 6, left: 6, margin: 0 }}>{candidate.assessorName}</Tag>
      )}
    </div>
  );
};
